import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import MarketplaceShell from './MarketplaceShell';
import MarketplaceErrorScreen from './MarketplaceErrorScreen';
import { clearDraft, loadDraft } from './wizardDraft';
import { MARKETPLACE_RUN_PATH, audienceStepState } from './marketplaceLinks';

/** The session draft's form state, but only when it belongs to this study. */
const draftFormDataFor = (studyId?: number): Record<string, unknown> | undefined => {
  const draft = loadDraft();
  return draft && draft.studyId === studyId ? draft.savedFormData : undefined;
};
import StepIndicator, { WIZARD_STEP_LABELS } from './StepIndicator';
import { labelForUseCase } from './useCaseMeta';
import { MktCard, FreeBadge, WizardNav } from './marketplaceUi';
import {
  getStudy, submitStudy, dollars,
  type Study, type StudySelection,
} from './marketplaceApi';

/**
 * Simulation review — the last look before a study runs. Used by BOTH the free
 * and paid flows: free studies get a "Run study" CTA, paid studies get
 * "Review & pay" with the cost and Stripe checkout. Surfaces the resolved Twin
 * segments (names highlighted) and a summary of the inputs the user entered.
 */
const ReviewScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const stateId = (location.state as { studyId?: number } | null)?.studyId;
  const studyId = stateId ?? (params.get('study') ? Number(params.get('study')) : undefined);

  const [study, setStudy] = useState<Study | null>(null);
  // Two DIFFERENT failures, two different treatments:
  // - loadError: the study couldn't be fetched (deleted, not owned, 5xx, or a
  //   cancelled Stripe checkout bouncing back to a stale id) — the page has
  //   nothing to show, so it gets a real error screen WITH a retry.
  // - submitError: the page is fine, only the submit failed — that stays inline
  //   next to the button instead of blowing the whole review away.
  // Both used to share one state rendered as a bare red transport string
  // ("GET /api/marketplace/studies/2096 failed: 404") in place of the page.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadStudy = useCallback(() => {
    if (!studyId) return;
    setLoadError(null);
    getStudy(studyId).then(setStudy).catch((e) => setLoadError(e.message));
  }, [studyId]);

  useEffect(() => {
    if (!studyId) { navigate('/marketplace/run', { replace: true }); return; }
    loadStudy();
  }, [studyId, navigate, loadStudy]);

  const onRun = async () => {
    if (!studyId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await submitStudy(studyId);
      // The wizard is done with — a stale draft must not resurrect it later (#73).
      clearDraft();
      if (res.checkout_url) { window.location.href = res.checkout_url; return; }
      // Chat is interactive — it has no batch run to watch, so it goes straight
      // into the live chat rather than the processing screen.
      if (study?.use_case === 'chat') {
        navigate(`/marketplace/studies/${studyId}/chat`, { replace: true });
        return;
      }
      navigate('/marketplace/run/processing', { state: { studyId } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start your study');
    } finally {
      setBusy(false);
    }
  };

  // Back must mean "the step BEFORE Review", not "wherever I came from". navigate(-1)
  // made it history-dependent: resuming a draft from My studies lands on Review with My
  // studies behind it, so Back bounced the user out of the study they were trying to
  // finish instead of letting them edit it.
  //
  // Chat is the exception — it has no Set-up step, and FillStudyScreen would create a
  // fresh chat study and send us straight back here. It goes to the audience step.
  const goBackToSetUp = () => {
    if (!study) { navigate('/marketplace/run'); return; }
    if (study.use_case === 'chat') {
      // Chat has no Set-up step — /fill would create a fresh chat study and bounce
      // straight back here. Go to the audience step instead, but carry the audience the
      // user already chose (otherwise they rebuild it from scratch) and the studyId, so
      // continuing REUSES this study rather than orphaning it as a duplicate draft.
      navigate(MARKETPLACE_RUN_PATH, {
        state: audienceStepState({ useCase: 'chat', selections: study.selections, studyId }),
      });
      return;
    }
    navigate('/marketplace/run/fill', {
      state: {
        useCase: study.use_case,
        selections: study.selections,
        twinCount: study.twin_count,
        paid: !study.is_free,
        // Reuse the SAME draft study rather than creating a duplicate.
        studyId,
        // Restore the form ONLY from the session draft, and only if that draft is for
        // THIS study. `study.inputs` is the SUBMITTED payload, a different shape from the
        // form's editable state — feeding it back would mis-populate the form. Without a
        // matching draft (e.g. resumed in a new tab) the form starts empty, but the study
        // and its audience are still reused.
        savedFormData: draftFormDataFor(studyId),
      },
    });
  };

  if (loadError) {
    const notFound = /\b404\b/.test(loadError);
    return (
      <MarketplaceErrorScreen
        title={notFound ? 'We couldn’t find this study' : 'We couldn’t load your review'}
        message={
          notFound
            ? 'This study may have been removed, or the link is out of date. You can start a new one.'
            : 'Something went wrong loading your study. Please try again in a moment.'
        }
        onRetry={loadStudy}
      />
    );
  }
  if (!study) return <MarketplaceShell maxWidth="max-w-2xl"><div className="text-[13px] text-[#6b6b6b]">Loading…</div></MarketplaceShell>;

  const selections = study.selections || [];
  const isFree = study.is_free;
  // Chat has no "Set up" step (no form to fill), so step 3 is never completed —
  // mark it skipped so it doesn't render a green check it didn't earn.
  const isChat = study.use_case === 'chat';

  return (
    <MarketplaceShell maxWidth="max-w-2xl">
      <StepIndicator step={4} labels={WIZARD_STEP_LABELS} skippedSteps={isChat ? [3] : []} />
      <h1 className="text-[22px] font-medium text-[#111]">{isFree ? 'Review your study' : 'Review & pay'}</h1>
      <p className="mt-1.5 text-[13px] text-[#6b6b6b]">
        {isChat
          ? 'One last look before your chat starts.'
          : 'One last look before your study runs.'}
      </p>

      <MktCard className="mt-5 divide-y divide-black/10 p-0">
        {/* Use case */}
        <div className="flex items-center justify-between p-5">
          <span className="text-[11px] uppercase tracking-[0.04em] text-[#999]">Use case</span>
          <span className="text-[13px] font-medium text-[#111]">{labelForUseCase(study.use_case)}</span>
        </div>

        {/* Audience — grouped by sub-category so identically-named cohorts
            (e.g. "Protein Optimizers" in several categories) stay unambiguous. */}
        <div className="p-5">
          <div className="mb-2.5 text-[11px] uppercase tracking-[0.04em] text-[#999]">Audience</div>
          <div className="flex flex-col gap-3.5">
            {groupBySubCategory(selections).map((group) => (
              <div key={group.slug}>
                <div className="mb-1.5 text-[12px] font-medium text-[#6b6b6b]">{group.label}</div>
                <ul className="flex flex-col gap-2">
                  {group.items.map((s, i) => (
                    <li key={i} className="flex items-center justify-between gap-3">
                      <TwinName name={s.name || prettySlug(s)} />
                      <span className="flex-shrink-0 text-[12px] text-[#6b6b6b]">{s.twin_count ?? '—'} Twins</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Inputs the user entered */}
        <InputsSummary study={study} />

        {/* Total + cost */}
        <div className="flex items-center justify-between p-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.04em] text-[#999]">Total AI Consumer Twins</div>
            <div className="text-[15px] font-medium text-[#111]">{study.twin_count}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.04em] text-[#999]">{isFree ? 'Cost' : 'Total cost'}</div>
            {isFree
              ? <div className="mt-0.5"><FreeBadge>Free</FreeBadge></div>
              : <div className="text-[20px] font-medium text-[#111]">{dollars(study.amount_cents)}</div>}
          </div>
        </div>
      </MktCard>

      <WizardNav
        onBack={goBackToSetUp}
        onContinue={onRun}
        continueDisabled={busy}
        continueLabel={
          busy
            ? (isFree ? (isChat ? 'Starting your chat…' : 'Starting your study…') : 'Starting checkout…')
            : isFree
            ? (isChat ? 'Start chat →' : 'Run study →')
            : `Pay ${dollars(study.amount_cents)} and ${isChat ? 'chat' : 'run'} →`
        }
        hint={error ? <span className="text-red-600">{error}</span> : undefined}
      />
    </MarketplaceShell>
  );
};

/** A Twin segment name, visually highlighted (teal chip) so it stands out. */
const TwinName: React.FC<{ name: string }> = ({ name }) => (
  <span className="inline-flex items-center rounded-md bg-[#E8FCFA] px-2 py-1 text-[13px] font-medium text-[#0B7C74]">
    {name}
  </span>
);

const prettySlug = (s: StudySelection): string => {
  const raw = s.archetype_id || s.sub_category || 'Selected Twins';
  return raw.replace(/__\d+$/, '').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const prettyCategory = (slug: string): string =>
  slug.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Groups selections by sub-category (preserving first-seen order) so the
 *  review shows which category each cohort belongs to. */
const groupBySubCategory = (
  selections: StudySelection[],
): { slug: string; label: string; items: StudySelection[] }[] => {
  const groups: { slug: string; label: string; items: StudySelection[] }[] = [];
  selections.forEach((s) => {
    const slug = s.sub_category || 'other';
    let group = groups.find((g) => g.slug === slug);
    if (!group) {
      group = { slug, label: s.sub_category ? prettyCategory(s.sub_category) : 'Other', items: [] };
      groups.push(group);
    }
    group.items.push(s);
  });
  return groups;
};

/** Per-use-case summary of the inputs the user filled in. Defensive: only
 *  renders sections that are present. */
const InputsSummary: React.FC<{ study: Study }> = ({ study }) => {
  const inputs = (study.inputs || {}) as Record<string, any>;
  const goal = (inputs.goal || inputs.goal_of_simulation || '') as string;
  const context = (inputs.context || '') as string;
  const questions: any[] = Array.isArray(inputs.questions) ? inputs.questions : [];

  const questionText = (q: any): string =>
    (typeof q === 'string' ? q : (q?.question_text || q?.text || q?.question || q?.title || '')) || '';

  const hasContent = goal || context || questions.length;
  if (!hasContent) {
    return (
      <div className="p-5">
        <div className="mb-1 text-[11px] uppercase tracking-[0.04em] text-[#999]">Your inputs</div>
        <p className="text-[13px] text-[#6b6b6b]">Your {study.use_case} inputs are saved and ready to run.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-5">
      {goal && (
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-[0.04em] text-[#999]">Goal</div>
          <p className="text-[13px] leading-relaxed text-[#374151]">{goal}</p>
        </div>
      )}
      {context && (
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-[0.04em] text-[#999]">Context</div>
          <p className="text-[13px] leading-relaxed text-[#374151]">{context}</p>
        </div>
      )}
      {questions.length > 0 && (
        <div>
          <div className="mb-1.5 text-[11px] uppercase tracking-[0.04em] text-[#999]">
            Questions <span className="text-[#bbb]">({questions.length})</span>
          </div>
          <ol className="list-decimal space-y-1 pl-5 text-[13px] leading-relaxed text-[#374151] marker:text-[#999]">
            {questions.map((q, i) => <li key={i}>{questionText(q) || <span className="text-[#999]">Question {i + 1}</span>}</li>)}
          </ol>
        </div>
      )}
    </div>
  );
};

export default ReviewScreen;
